import React, { useEffect } from "react";
import { Box, Fade, Slide, IconButton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { set } from "../../store/globalSlice";
import { CrossIcon } from "../../assets/icons";
import { HourglassLabelIcon } from "../IconLabel";
import { checkEmptyObj } from "../../utils";
import { useLocation } from "react-router-dom";

export default function ApprovalPendingModal() {
  const { t, i18n } = useTranslation("approvalPendingModal");
  const isRTL = i18n.language === "ar";
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user);
  const showApprovalPendingModal = useSelector((state: RootState) => state.global.showApprovalPendingModal);
  const isQa = useSelector((state: RootState) => state.global.qaClient);
  const handleClose = () => {
    dispatch(set({ showApprovalPendingModal: false }));
    sessionStorage.removeItem("congrats");
  };
  const location = useLocation();
  const isOnCongrats = sessionStorage.getItem("congrats") === "true";
  const displayLoggedInModal = !checkEmptyObj(user) && !isOnCongrats;

  return (
    <>
      <Fade in={showApprovalPendingModal}>
        <Box
          className="approval-pending-modal-backdrop"
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={handleClose}
        />
      </Fade>
      <Slide direction="up" in={showApprovalPendingModal} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none", // Allow clicks to pass through to backdrop
          }}
        >
          <Box
            sx={{
              maxWidth: "420px",
              width: "90%",
              maxHeight: "90vh",
              overflow: "auto",
              borderRadius: "16px",
              boxShadow: "0px 12px 32px 0px rgba(0, 0, 0, 0.15)",
              backgroundColor: "var(--body-bg)",
              position: "relative",
              pointerEvents: "auto", // Re-enable pointer events for the modal content
              padding: "32px",
              textAlign: "center",
              "@media (max-width: 600px)": {
                width: "95%",
                maxWidth: "none",
                padding: "24px",
              },
            }}
          >
            <IconButton
              onClick={handleClose}
              sx={{
                position: "absolute",
                top: 8,
                ...(isRTL ? { left: 8 } : { right: 8 }),
                zIndex: 1,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 1)",
                },
              }}
            >
              <CrossIcon />
            </IconButton>

            {/* Hourglass Icon at the top */}
            <Box sx={{ marginBottom: "24px" }}>
              <HourglassLabelIcon />
            </Box>

            {/* Title */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "var(--primary-dark)",
                mb: 3,
                fontSize: "18px",
                lineHeight: "150%",
              }}
            >
              {t(`${isQa ? (displayLoggedInModal ? "loggedIn" : "qa") : "nonQa"}.title`)}
            </Typography>

            {/* Subtitle */}
            <Typography
              sx={{
                fontWeight: 700,
                color: "var(--font-darkest)",
                mb: 3,
                fontSize: "14px",
                lineHeight: "24px",
              }}
            >
              {isQa && !displayLoggedInModal ? t("qa.subtitle") : null}
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                color: "var(--font-default)",
                fontSize: "14px",
                lineHeight: "24px",
                fontWeight: 500,
              }}
            >
              {t(`${isQa ? (displayLoggedInModal ? "loggedIn" : "qa") : "nonQa"}.description`)}
            </Typography>
          </Box>
        </Box>
      </Slide>
    </>
  );
}
